'use client'

import { cn } from '@/lib/utils'
import { MultiSelectSearchableDropdown } from '@frontend/components/enhanced/multi-select-searchable-dropdown'
import { Check, ChevronsUpDown, Trash2 } from 'lucide-react'
import * as React from 'react'
import { Button } from '../base/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '../base/command'
import { Popover, PopoverContent, PopoverTrigger } from '../base/popover'

interface Option {
  value: string | number
  name: string
  logo?: string | React.ReactNode // Support for logo/icon
  id?: string | number // Optional unique identifier for React keys
}

export interface SearchableDropdownProps {
  name?: string
  items?: Option[]
  options?: Option[] // Alternative prop name for compatibility
  value?: string | number | string[] // Support both single and multi
  onSelect?: (value: string | number | string[]) => void
  onChange?: (value: string | number | string[]) => void // Alternative prop name for compatibility
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  className?: string
  disabledValue?: string | number
  isMultiple?: boolean
  inputVariant?: string
  error?: boolean
  errorMessage?: string
  initialDisplayCount?: number // How many items to show initially
  searchThreshold?: number // Minimum characters to trigger search
  enablePagination?: boolean // Enable load more functionality
  clearButton?: boolean
  maxBadgeDisplay?: number // How many badges to show before "+N more"
  keepConsistentWidth?: boolean // specify if the container and popover should have the same width
  disabledValues?: (string | number)[]
}

export function SearchableDropdown(props: SearchableDropdownProps) {
  if (props.isMultiple) {
    return <MultiSelectSearchableDropdown {...props} />
  }

  return <SingleSelectSearchableDropdown {...props} />
}

function SingleSelectSearchableDropdown(props: SearchableDropdownProps) {
  const {
    name,
    items = [],
    options = [],
    value,
    onSelect,
    onChange,
    label,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    className,
    disabledValue,
    inputVariant,
    error = false,
    errorMessage,
    initialDisplayCount = 20,
    searchThreshold = 0,
    enablePagination = true,
    clearButton = true,
    maxBadgeDisplay = 3,
    keepConsistentWidth = false,
    disabledValues = []
  } = props

  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState('')
  const [displayCount, setDisplayCount] = React.useState(initialDisplayCount)

  // Use items if provided, otherwise fall back to options
  const optionsList = items.length > 0 ? items : options

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchValue || searchValue.length < searchThreshold) {
      return optionsList
    }

    return optionsList.filter((option) =>
      option.name.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [optionsList, searchValue, searchThreshold])

  // Apply display limit for performance
  const displayedOptions = React.useMemo(() => {
    if (searchValue && searchValue.length >= searchThreshold) {
      // When searching, show all results (already filtered)
      return filteredOptions
    }
    // When not searching, limit initial display
    return filteredOptions.slice(0, displayCount)
  }, [filteredOptions, displayCount, searchValue, searchThreshold])

  // Use onSelect if provided, otherwise fall back to onChange
  const handleChange = onSelect || onChange

  const handleSelect = (selectedValue: string | number | null) => {
    if (!handleChange) {
      console.error('handleChange function not defined for searchable dropdown')
      return
    }

    const currentValue = value ? value?.toString() : ''
    const newValue = selectedValue ? selectedValue?.toString() : ''

    // If the same value is selected, deselect it
    handleChange(newValue === currentValue ? '' : newValue)

    setOpen(false)
    setSearchValue('') // Reset search when selection is made
    setDisplayCount(initialDisplayCount) // Reset display count
  }

  const removeItem = (itemToRemove: string) => {
    if (handleChange && Array.isArray(value)) {
      handleChange(value.filter((v) => v !== itemToRemove))
    }
  }

  const handleLoadMore = () => {
    setDisplayCount((prev) =>
      Math.min(prev + initialDisplayCount, filteredOptions.length)
    )
  }

  const handleSearchChange = (search: string) => {
    setSearchValue(search)
    setDisplayCount(initialDisplayCount) // Reset display count when searching
  }

  const clearSelection = () => {
    if (!handleChange) {
      console.error('handleChange function not defined for searchable dropdown')
      return
    }
    handleChange('')

    setSearchValue('') // Reset search when selection is made
    setDisplayCount(initialDisplayCount) // Reset display count
  }

  const selectedOption = optionsList.find(
    (option) => option.value?.toString() === value?.toString()
  )

  const hasMore =
    !searchValue && displayedOptions.length < filteredOptions.length

  const hasSelection =
    (Array.isArray(value) && value.length > 0) ||
    (!Array.isArray(value) && !!value)

  return (
    <div className={cn('flex flex-col', className)}>
      {label && (
        <label htmlFor={name} className='text-xs'>
          {label}
        </label>
      )}
      <div className='flex gap-1'>
        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant='outline'
              role='combobox'
              aria-expanded={open}
              className={cn(
                'border-border min-w-0 flex-1 justify-between px-2',
                error && 'border-red-500'
              )}
            >
              <div className='flex min-w-0 flex-1 items-center gap-1'>
                <div className='flex min-w-0 flex-1 items-center gap-2 overflow-hidden'>
                  <span
                    className={`truncate text-xs ${
                      !selectedOption?.name && placeholder
                        ? 'text-muted-foreground'
                        : ''
                    }`}
                  >
                    {selectedOption?.name || placeholder}
                  </span>
                </div>
              </div>
              <ChevronsUpDown className='ml-1 h-3 w-3 shrink-0 opacity-50' />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className='p-0'
            align='start'
            style={{
              zIndex: 9999,
              width: keepConsistentWidth
                ? 'var(--radix-popover-trigger-width)'
                : '100%'
            }}
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={searchPlaceholder}
                className='h-8'
                value={searchValue}
                onValueChange={handleSearchChange}
              />
              <CommandList className='max-h-60 overflow-y-auto'>
                <CommandEmpty>No options found.</CommandEmpty>
                <CommandGroup>
                  {displayedOptions.map((option, index) => (
                    <CommandItem
                      key={option.id || option.value || index}
                      value={`${option.name}|||${option.value}`}
                      onSelect={() => handleSelect(option.value)}
                      className='cursor-pointer text-xs'
                      disabled={
                        option.value === disabledValue ||
                        disabledValues?.includes(option.value)
                      }
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3 w-3',
                          value?.toString() === option.value?.toString()
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      <div className='flex flex-1 items-center gap-2'>
                        <span>{option.name}</span>
                      </div>
                    </CommandItem>
                  ))}
                  {hasMore && enablePagination && (
                    <CommandItem
                      key='load-more'
                      onSelect={handleLoadMore}
                      className='text-muted-foreground mt-1 border-t pt-1 text-center text-xs'
                    >
                      Load more... (
                      {filteredOptions.length - displayedOptions.length}{' '}
                      remaining)
                    </CommandItem>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Clear Button */}
        {clearButton && hasSelection && (
          <Button
            variant='outline'
            type='button'
            size='sm'
            onClick={clearSelection}
            className='h-[36px] w-8 flex-shrink-0 bg-transparent p-0 hover:border-red-200 hover:bg-red-50'
            title='Clear selection'
          >
            <Trash2 className='h-3 w-3 text-red-500' />
          </Button>
        )}
      </div>
      {error && errorMessage && (
        <p className='text-xs text-red-500'>{errorMessage}</p>
      )}
    </div>
  )
}
